'use client';

import { c as _c } from "react/compiler-runtime";
import type { LinkProps as NextLinkProps } from 'next/link';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import { classifyLink } from '@sola/utils/url.ts';
import { useRouter } from '../../utils/hooks/useRouter.ts';
export type LinkProps = NextLinkProps & React.PropsWithChildren & {
  id?: string;
  className?: string;
  isDisabled?: boolean;
  title?: string;
  download?: boolean;
  type?: 'button' | 'submit' | 'reset';
  target?: string;
  rel?: string;
};

/**
 * Resolves a Next.js href (string or UrlObject) into a plain URL string
 * suitable for a native `<a>` tag.  Prevents `[object Object]` in the DOM.
 */
const resolveHref = (href: LinkProps['href']): string => {
  if (typeof href === 'string') {
    return href;
  }
  const {
    pathname,
    query,
    hash
  } = href ?? {};
  let url = pathname ?? '';
  if (query) {
    const params = typeof query === 'string' ? query : new URLSearchParams(query as Record<string, string>).toString();
    if (params) {
      url += `?${params}`;
    }
  }
  if (hash) {
    url += hash.startsWith('#') ? hash : `#${hash}`;
  }
  return url;
};
export const Link: React.FC<LinkProps> = t0 => {
  const $ = _c(12);
  const {
    isDisabled,
    ...props
  } = t0;
  const {
    RouterComponent
  } = useRouter();
  const handleClick = e => {
    trackClick(props.title || getTextFromReactNode(props.children) || "Unknown", resolveHref(props.href), props.className, props.id, "Link");
    if (props.onClick) {
      props.onClick(e);
    }
  };
  if (!RouterComponent || isDisabled) {
    let t1;
    if ($[0] !== isDisabled) {
      t1 = isDisabled && {
        "aria-disabled": true,
        role: "link",
        href: undefined
      };
      $[0] = isDisabled;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return <a {...props as React.ComponentProps<'a'>} {...t1} />;
  }
  const {
    navigation,
    target,
    url
  } = classifyLink(resolveHref(props.href));
  if (navigation === "full-load") {
    const resolvedTarget = props.target || target;
    const opensOtherContext = !["_self", "_parent", "_top"].includes(resolvedTarget);
    const explicitRel = props.rel?.trim().split(/\s+/).filter(Boolean) ?? [];
    const rel = opensOtherContext ? [...new Set([...explicitRel, "noopener", "noreferrer"])].join(" ") : props.rel;
    const t1 = props as React.ComponentProps<'a'>;
    let t2;
    if ($[2] !== handleClick || $[3] !== rel || $[4] !== resolvedTarget || $[5] !== t1 || $[6] !== url) {
      t2 = <a {...t1} href={url} target={resolvedTarget} rel={rel} onClick={handleClick} />;
      $[2] = handleClick;
      $[3] = rel;
      $[4] = resolvedTarget;
      $[5] = t1;
      $[6] = url;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== RouterComponent || $[9] !== handleClick || $[10] !== props) {
    t1 = <RouterComponent {...props} onClick={handleClick} />;
    $[8] = RouterComponent;
    $[9] = handleClick;
    $[10] = props;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
};